import { RollingNumber } from './RollingNumber';
import { currencyAffix } from '../currency';
import { useIsSleek } from '../theme';

interface BuybackReceivedLineProps {
  /** Gross buyback price credited to the user's balance. */
  amount: number;
}

/**
 * Big "+$X.XX" line shown after the buyback is accepted — the amount
 * added to the balance. Reuses the slot-machine `RollingNumber` for a
 * value lands with the same odometer language as the rest of the
 * winner screen.
 */
export function BuybackReceivedLine({ amount }: BuybackReceivedLineProps) {
  const { prefix, suffix } = currencyAffix();
  // Sleek (partner Figma 2026-10-06): a flat green, semibold, no glow.
  const isSleek = useIsSleek();
  return (
    <span
      data-co-component="WinnerStepProfit"
      className={isSleek ? 'inline-block font-semibold tracking-[0.37px]' : 'inline-block font-bold tracking-[-0.6px]'}
      style={{
        color: isSleek
          ? 'var(--co-WinnerStepProfit-valueColor, #16A34A)'
          : 'var(--co-WinnerStepProfit-valueColor, var(--color-brand-primary, #22E11C))',
        fontFamily:
          'var(--co-WinnerStepProfit-font, "Inter"), Inter, sans-serif',
        // Same svh cap + unitless line-height as FmvOnly, so the credited
        // amount lands in the exact slot the FMV number occupied.
        fontSize: `min(var(--co-WinnerStepProfit-size, 44px), ${isSleek ? '6.3svh' : '5.2svh'})`,
        lineHeight: 1.27,
        // Glow via `filter` on the whole line, NOT `text-shadow`: a
        // text-shadow is inherited by RollingNumber's per-digit
        // overflow-hidden windows and gets clipped into visible rectangles
        // ("squares") behind each digit. `drop-shadow` applies to the
        // composited glyphs after clipping, so it haloes the numbers cleanly.
        ...(isSleek ? {} : { filter: 'drop-shadow(0 0 12px rgba(34,225,28,0.5))' }),
      }}
    >
      +{prefix}<RollingNumber value={amount} start instant={false} />{suffix}
    </span>
  );
}
